import {RouteComponentProps, Router} from "react-router"
import * as React from "react"
import {Auth0Config, Auth0ConfigFragFragment, Booker as BOOKER} from "../graphql/queries"
import {errorUnionToEither, handleError} from "../graphql/errors"
import {Button, Container, Header, Loader, Sidebar} from "semantic-ui-react"
import {Loading, loadingIfNull, undefinedToNull} from "../tsutil"
import {Redirect, Route, Switch} from "react-router-dom"
import {Admin} from "./Admin"
import {CommonPlacesAdmin} from "./CommonPlacesAdmin"
import {DriversAdmin} from "./DriversAdmin"
import {CusoonSidebar, CusoonTopMenu} from "./CusoonNav"
import {BookingsList, BookingsListState, defaultBookingsListState} from "./BookingsList"
import {ModalRoot} from "./ModalRoot"
import {Booking} from "./Booking"
import {Article} from "./About"
import {Profile} from "./Profile"
import {JourneyPage} from "./JourneyPage"
import {InvoicePage} from "./InvoicePage"
import {DriverSignup} from "./DriverSignup"
import {GraphQL} from "../graphql"
import {History} from "history"
import {history, ModalStore, WithModalStore} from "../App"
import {AddBooking} from "./AddBooking"
import {RepeatReturnBooking} from "./RepeatReturnBooking"
import {OutOfDateAppWarning} from "./OutOfDateAppWarning"
import {ErrorBoundary} from "../ErrorBoundary"
import {PayerSignup} from "./PayerSignup"
import {TestModeBand} from "./TestModeBand"
import {Logo} from "./Logo"
import {NewBookerLogin} from "./NewBookerLogin"
import {BookingsSummary} from "./BookingsSummary"
import {PricingPage} from "./PricingPage"
import {Booker} from "../model/BookingTypes"
import ScrollToTop from "./ScrollToTop"
import {Location} from "history"
import {getUrlLocaleId} from "../urlLocale"
import {Auth0LockRedirect, getUrlEmailAndReturnUrl} from "./Auth0LockLink"

interface Props extends WithModalStore {
  history: History
}

interface State {
  bookingsList: BookingsListState
  sidebarOpen: boolean
}

type AllProps = Props & BookerProp & Auth0ConfigProp

function route(
  path: string,
  Render: React.ReactType<RouteComponentProps<any>>,
) {
  return <Route
    exact
    path={path}
    render={props => <>
      <Render {...props} />
    </>}
  />;
}

class Cusoon extends React.Component<AllProps, State> {
  constructor(props: AllProps) {
    super(props)

    this.state = {
      bookingsList: defaultBookingsListState(),
      sidebarOpen: false,
    }
  }

  get modalStore(): ModalStore {
    return this.props.modalStore
  }

  setBookingsListState = (state: Partial<BookingsListState>) =>
    this.setState({
      bookingsList: {...this.state.bookingsList, ...state},
    })

  bookingsList = () => (
    <BookingsList
      modalStore={this.modalStore}
      state={this.state.bookingsList}
      setState={this.setBookingsListState}
    />
  )

  pricingPage = (props: {location: Location}) => (
    <PricingPage urlLocaleId={getUrlLocaleId(props.location)} modalStore={this.modalStore} />
  )

  addBooking = (props: {location: Location}) => (
    <AddBooking urlLocaleId={getUrlLocaleId(props.location)} modalStore={this.modalStore} />
  )

  editBooking = ({
    match: {
      params: {num},
    },
  }: RouteComponentProps<{num: string}>) => (
    <Booking modalStore={this.modalStore} bookingNumber={num} />
  )

  editJourney = ({
    match: {
      params: {num},
    },
  }: RouteComponentProps<{num: string}>) => (
    <JourneyPage modalStore={this.modalStore} journeyId={num} />
  )

  editProfile = ({
    match: {
      params: {id},
    },
  }: RouteComponentProps<{id: string}>) => {
    const {booker} = this.props
    const isAdmin = Boolean(booker && booker.isAdmin)
    return <Profile modalStore={this.modalStore} forBookerId={id} isAdmin={isAdmin} />
  }

  payInvoice = ({
    match: {
      params: {id},
    },
  }: RouteComponentProps<{id: string}>) => (
    <InvoicePage modalStore={this.modalStore} invoiceId={id} />
  )

  driverSignup = ({
    match: {
      params: {id},
    },
  }: RouteComponentProps<{id: string}>) => <DriverSignup modalStore={this.modalStore} id={id} />

  payerSignup = ({
    match: {
      params: {id},
    },
  }: RouteComponentProps<{id: string}>) => <PayerSignup modalStore={this.modalStore} id={id} />

  logIn = ({location}: RouteComponentProps) => {
    const params = getUrlEmailAndReturnUrl(location)
    return <Auth0LockRedirect
      email={params.email}
      returnUrl={params.returnUrl}
      initialScreen="login"
    />
  }

  signUp = ({location}: RouteComponentProps) => {
    const params = getUrlEmailAndReturnUrl(location)
    return <Auth0LockRedirect
      email={params.email}
      returnUrl={params.returnUrl}
      initialScreen="signUp"
    />
  }

  forgotPassword = ({location}: RouteComponentProps) => {
    const params = getUrlEmailAndReturnUrl(location)
    return <Auth0LockRedirect
      email={params.email}
      returnUrl={params.returnUrl}
      initialScreen="forgotPassword"
    />
  }

  repeatBooking = ({
    match: {
      params: {num},
    },
    location,
  }: RouteComponentProps<{num: string}>) => (
    <RepeatReturnBooking
      urlLocaleId={getUrlLocaleId(location)}
      mode="repeat"
      modalStore={this.modalStore}
      bookingNumber={num}
    />
  )

  returnBooking = ({
    match: {
      params: {num},
    },
    location,
  }: RouteComponentProps<{num: string}>) => (
    <RepeatReturnBooking
      urlLocaleId={getUrlLocaleId(location)}
      mode="return"
      modalStore={this.modalStore}
      bookingNumber={num}
    />
  )

  notFoundPage = () => {
    return <Article>
      <Header as="h2">404 Not Found</Header>
      <p>
        The page was not found
      </p>
      <Button onClick={() => {
        history.push("/")
      }}>
        Go Home
      </Button>
    </Article>
  }

  newBookerLogin = ({
    match: {
      params: {secret, num},
    },
  }: RouteComponentProps<{secret: string; num?: string}>) => (
    <NewBookerLogin
      newBookerSecret={secret}
      bookingNumber={undefinedToNull(num)}
      modalStore={this.modalStore}
    />
  )

  onNavClick = () => {
    if (this.state.sidebarOpen) {
      this.setState({sidebarOpen: false})
    }
  }

  render() {
    const {history, modalStore, booker, auth0Config} = this.props
    const {sidebarOpen} = this.state

    return (
      <>
        <Router history={history}>
          <div>
            <Logo />
            <TestModeBand />
            <ScrollToTop />
            <Route
              path="/"
              render={({location}) => (
                <CusoonTopMenu
                  location={location}
                  booker={booker}
                  auth0Config={auth0Config}
                  onClick={this.onNavClick}
                  onOpenOrClose={() => this.setState({sidebarOpen: !sidebarOpen})}
                />
              )}
            />
            <Sidebar.Pushable>
              <Route
                path="/"
                render={({location}) => (
                  <CusoonSidebar
                    location={location}
                    onClick={this.onNavClick}
                    sidebarOpen={sidebarOpen}
                    booker={booker}
                  />
                )}
              />
              <Sidebar.Pusher dimmed={sidebarOpen}>
                <OutOfDateAppWarning />
                <ModalRoot modalStore={modalStore} />
                <Container
                  as="main"
                  style={{
                    // We want this small so the Loaders are visible in the mobile viewport yet we
                    // want it big so the sidebar doesn't get cut off and the suggestions in the
                    // "To" and "From" places dropdown don't go off the bottom of the screen.  But
                    // the actual problem is that my CSS is not good enough to find a real solution.
                    minHeight: "800px",
                    // Some of the content pages were pushing right up against the bottom of the
                    // page, so this adds some margin.
                    marginBottom: "2em",
                    marginTop: "1rem",
                  }}
                >
                  {/* This ErrorBoundary is here so we can catch errors in the current view without
             making the nav bar disappear. */}
                  <ErrorBoundary>
                    <Switch children={[
                      route("/", () => <Redirect to="/bookings/add"/>),
                      route("/pricing", this.pricingPage),

                      route("/admin", () => <Admin modalStore={modalStore}/>),
                      route("/admin/common-places", () => <CommonPlacesAdmin modalStore={modalStore}/>),
                      route("/admin/drivers", () => <DriversAdmin modalStore={modalStore}/>),
                      route("/admin/bookings-summary", () => <BookingsSummary/>),

                      route("/bookings", this.bookingsList),
                      route("/bookings/add", this.addBooking),
                      route("/booking/:num", this.editBooking),
                      route("/booking/:num/repeat", this.repeatBooking),
                      route("/booking/:num/return", this.returnBooking),
                      route("/journey/:num", this.editJourney),
                      route("/profile/:id", this.editProfile),
                      route("/invoice/:id", this.payInvoice),
                      route("/driver-signup/:id", this.driverSignup),
                      route("/booking-payment/:id", this.payerSignup),
                      route("/new-booker-login/:secret", this.newBookerLogin),
                      route("/new-booker-login/:secret/booking/:num", this.newBookerLogin),
                      route("/sign-up", this.signUp),
                      route("/log-in", this.logIn),
                      route("/forgot-password", this.forgotPassword),

                      <Route exact path="/forgotpassword">
                        {({location: {search, hash}}) => <Redirect to={"/forgot-password" + search + hash}/>}
                      </Route>,
                      <Route exact path="/signup">
                        {({location: {search, hash}}) => <Redirect to={"/sign-up" + search + hash}/>}
                      </Route>,
                      <Route exact path="/login">
                        {({location: {search, hash}}) => <Redirect to={"/log-in" + search + hash}/>}
                      </Route>,

                      route("/404", this.notFoundPage),
                      <Redirect to="/404"/>,
                    ]}/>
                  </ErrorBoundary>
                </Container>
              </Sidebar.Pusher>
            </Sidebar.Pushable>
          </div>
        </Router>
      </>
    )
  }
}

interface BookerProp {
  booker: Booker | null
}

function WithBooker(props: Props & Auth0ConfigProp) {
  return (
    <GraphQL.Query
      query={BOOKER}
      variables={{}}
      render={({data}) => {
        if (data !== null) {
          return handleError(errorUnionToEither(data.booker), (data) => (
            <Cusoon {...props} booker={data.booker} />
          ))
        }
        // This should not happen, as Booker is loaded in InitializeSession
        return <Loader active />
      }}
    />
  )
}

interface Auth0ConfigProp {
  auth0Config: Loading<Auth0ConfigFragFragment>
}

function WithAuth0Config(props: Props) {
  return (
    <GraphQL.Query
      query={Auth0Config}
      variables={{}}
      render={({data}) => {
        return (
          <WithBooker
            {...props}
            auth0Config={loadingIfNull(data).map((data) => data.auth0Config)}
          />
        )
      }}
    />
  )
}

export {WithAuth0Config as Cusoon}



// WEBPACK FOOTER //
// ./src/components/Cusoon.tsx